/* assets/wyrd-commercial.js */
(() => {
/* Wyrd Games commercial storefront interactions for Shopify Flow. */

const WyrdCommerce = (() => {
  const localeRoot = () => window.Shopify?.routes?.root || '/';
  const cartAddUrl = () => window.routes?.cart_add_url || `${localeRoot()}cart/add.js`;
  const cartUrl = () => window.routes?.cart_url || `${localeRoot()}cart`;

  async function readJson(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status === 422) {
      const message = data.description || data.message || 'This item could not be added to the cart.';
      throw new Error(message);
    }
    return data;
  }

  async function updateCartCount() {
    try {
      const response = await fetch(`${localeRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (window.wetheme?.updateCartCount) {
        window.wetheme.updateCartCount(cart);
      } else {
        document.querySelectorAll('[data-cart-count-indicator]').forEach((indicator) => {
          indicator.textContent = cart.item_count;
          indicator.classList.toggle('hide', cart.item_count === 0);
          indicator.toggleAttribute('hidden', cart.item_count === 0);
        });
      }
    } catch (error) {
      console.error('Unable to refresh the cart count.', error);
    }
  }

  function openNativeCart(responseJson) {
    const pageContainer = document.getElementById('PageContainer');
    if (pageContainer?.dataset.cartType !== 'drawer') return false;

    syncNativeCart(responseJson);
    eventBus.emit('open:cart:drawer', { scrollToTop: true });
    return true;
  }

  function syncNativeCart(responseJson) {
    const pageContainer = document.getElementById('PageContainer');
    if (pageContainer?.dataset.cartType !== 'drawer') return false;

    if (responseJson?.sections) {
      eventBus.emit('update:cart:drawer', responseJson);
    } else {
      eventBus.emit('render:cart:drawer');
    }

    return true;
  }

  function updateCartSilently(responseJson) {
    updateCartCount();
    syncNativeCart(responseJson);
  }

  function updateAndOpenCart(responseJson) {
    updateCartCount();

    if (openNativeCart(responseJson)) return;

    window.location.assign(cartUrl());
  }

  function moneyFromCents(cents, currencyCode) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  function focusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
  }

  return {
    cartAddUrl,
    localeRoot,
    moneyFromCents,
    readJson,
    updateAndOpenCart,
    updateCartSilently,
    updateCartCount,
    focusableElements,
  };
})();

class WyrdProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-wyrd-product-form]');
    this.button = this.querySelector('[data-wyrd-submit]');
    this.buttonLabel = this.querySelector('[data-wyrd-submit-label]');
    this.status = this.querySelector('[data-wyrd-form-status]');
    this.variantSelect = this.querySelector('[data-wyrd-variant-select]');
    this.priceTarget = this.querySelector('[data-wyrd-form-price]');
    this.initialButtonText = this.buttonLabel?.textContent.trim() || 'Add to cart';
    this.addingButtonText = this.form?.dataset.wyrdAddingLabel || 'Adding...';
    this.addedButtonText = this.form?.dataset.wyrdAddedLabel || 'Added';
    this.successMessage = this.form?.dataset.wyrdSuccessMessage || 'Added to cart.';
    this.statusTimer = 0;
    this.processing = false;

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    if (!this.form || !this.button) return;
    this.form.addEventListener('submit', this.handleSubmit);
    this.variantSelect?.addEventListener('change', this.handleVariantChange);
    this.handleVariantChange();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.variantSelect?.removeEventListener('change', this.handleVariantChange);
    window.clearTimeout(this.statusTimer);
  }

  handleVariantChange() {
    if (!this.variantSelect) return;
    const option = this.variantSelect.selectedOptions[0];
    const available = option?.dataset.available !== 'false';
    this.button.disabled = !available;

    if (this.priceTarget && option?.dataset.priceFormatted) {
      this.priceTarget.textContent = option.dataset.priceFormatted;
    }
  }

  setStatus(message, state = 'success') {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.state = state;

    if (this.buttonLabel && state === 'success' && message) {
      this.buttonLabel.textContent = this.addedButtonText;
    } else if (this.buttonLabel && !message && !this.processing) {
      this.buttonLabel.textContent = this.initialButtonText;
    }
  }

  setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.button.setAttribute('aria-busy', String(isLoading));
    if (this.buttonLabel) {
      if (isLoading) {
        this.buttonLabel.textContent = this.addingButtonText;
      } else if (this.status?.dataset.state === 'success' && this.status.textContent.trim()) {
        this.buttonLabel.textContent = this.addedButtonText;
      } else {
        this.buttonLabel.textContent = this.initialButtonText;
      }
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.processing) return;

    this.processing = true;
    this.setStatus('');
    this.setLoading(true);

    const formData = new FormData(this.form);
    if (!formData.has('sections')) formData.set('sections', 'cart-drawer');
    if (!formData.has('sections_url')) formData.set('sections_url', window.location.pathname);

    try {
      const response = await fetch(WyrdCommerce.cartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const responseJson = await WyrdCommerce.readJson(response);

      WyrdCommerce.updateCartSilently(responseJson);
      this.setStatus(this.successMessage);
      this.dispatchEvent(
        new CustomEvent('wyrd:product-added', {
          bubbles: true,
          detail: responseJson,
        })
      );

      window.clearTimeout(this.statusTimer);
      this.statusTimer = window.setTimeout(() => this.setStatus(''), 2400);
    } catch (error) {
      this.setStatus(error.message, 'error');
    } finally {
      this.processing = false;
      this.setLoading(false);
      this.handleVariantChange();
    }
  }
}

if (!customElements.get('wyrd-product-form')) {
  customElements.define('wyrd-product-form', WyrdProductForm);
}

class WyrdProductCard extends HTMLElement {
  constructor() {
    super();
    this.openButton = this.querySelector('[data-wyrd-sheet-open]');
    this.sheet = this.querySelector('[data-wyrd-variant-sheet]');
    this.closeButtons = this.querySelectorAll('[data-wyrd-sheet-close]');
    this.lastFocused = null;
    this.closeTimer = null;
    this.sheetOrigin = null;

    this.openSheet = this.openSheet.bind(this);
    this.closeSheet = this.closeSheet.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleProductAdded = this.handleProductAdded.bind(this);
  }

  connectedCallback() {
    if (!this.sheet || !this.openButton) return;
    this.openButton.addEventListener('click', this.openSheet);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.closeSheet));
    this.sheet.addEventListener('keydown', this.handleKeydown);
    this.sheet.addEventListener('wyrd:product-added', this.handleProductAdded);
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.openSheet);
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.closeSheet));
    this.sheet?.removeEventListener('keydown', this.handleKeydown);
    this.sheet?.removeEventListener('wyrd:product-added', this.handleProductAdded);
    window.clearTimeout(this.closeTimer);

    if (this.sheet?.parentElement === document.body) {
      if (this.sheetOrigin?.isConnected) {
        this.sheetOrigin.after(this.sheet);
      } else {
        this.sheet.remove();
      }
    }
  }

  openSheet() {
    window.clearTimeout(this.closeTimer);
    this.lastFocused = document.activeElement;

    /*
     * Swiper uses transformed, overflow-clipped tracks. A fixed sheet left
     * inside a recommendation slide can therefore be clipped to that card.
     * Portal only the open sheet to body, preserving an origin marker so it can
     * return without rebuilding the form or losing its listeners.
     */
    if (!this.sheetOrigin) {
      this.sheetOrigin = document.createComment('wyrd-variant-sheet-origin');
      this.sheet.before(this.sheetOrigin);
    }
    if (this.sheet.parentElement !== document.body) {
      document.body.append(this.sheet);
    }

    this.sheet.hidden = false;
    this.sheet.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wyrd-sheet-open');

    requestAnimationFrame(() => {
      this.sheet.classList.add('is-open');
      const focusables = WyrdCommerce.focusableElements(this.sheet);
      focusables[0]?.focus();
    });
  }

  closeSheet() {
    if (!this.sheet || this.sheet.hidden) return;
    this.sheet.classList.remove('is-open');
    this.sheet.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wyrd-sheet-open');

    this.closeTimer = window.setTimeout(() => {
      this.sheet.hidden = true;
      if (this.sheetOrigin?.isConnected) {
        this.sheetOrigin.after(this.sheet);
      }
      if (this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
    }, 220);
  }

  handleProductAdded() {
    window.setTimeout(this.closeSheet, 350);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSheet();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusables = WyrdCommerce.focusableElements(this.sheet);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('wyrd-product-card')) {
  customElements.define('wyrd-product-card', WyrdProductCard);
}

class WyrdBookshelf extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-wyrd-bookshelf-track]');
    this.previousButton = this.querySelector('[data-wyrd-bookshelf-previous]');
    this.nextButton = this.querySelector('[data-wyrd-bookshelf-next]');
    this.controls = this.querySelector('.wyrd-bookshelf__controls');
    this.handlePrevious = () => this.scrollByPage(-1);
    this.handleNext = () => this.scrollByPage(1);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.updateControls = this.updateControls.bind(this);
  }

  connectedCallback() {
    if (!this.track) return;
    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.updateControls, { passive: true });
    this.track.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.updateControls);
    requestAnimationFrame(this.updateControls);
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.track?.removeEventListener('scroll', this.updateControls);
    this.track?.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.updateControls);
  }

  scrollByPage(direction) {
    if (!this.track) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({
      left: direction * Math.max(280, this.track.clientWidth * 0.82),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  handleKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.scrollByPage(event.key === 'ArrowLeft' ? -1 : 1);
  }

  updateControls() {
    if (!this.track) return;
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const hasOverflow = maxScroll > 4;
    if (this.controls) this.controls.hidden = !hasOverflow;
    if (this.previousButton) this.previousButton.disabled = !hasOverflow || this.track.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = !hasOverflow || this.track.scrollLeft >= maxScroll - 2;
  }
}

if (!customElements.get('wyrd-bookshelf')) {
  customElements.define('wyrd-bookshelf', WyrdBookshelf);
}

class WyrdBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.items = Array.from(this.querySelectorAll('[data-wyrd-bundle-item]'));
    this.button = this.querySelector('[data-wyrd-bundle-submit]');
    this.total = this.querySelector('[data-wyrd-bundle-total]');
    this.count = this.querySelector('[data-wyrd-bundle-count]');
    this.status = this.querySelector('[data-wyrd-bundle-status]');
    this.currency = this.dataset.currency || 'USD';
    this.processing = false;

    this.updateSummary = this.updateSummary.bind(this);
    this.submitBundle = this.submitBundle.bind(this);
  }

  connectedCallback() {
    this.items.forEach((item) => {
      item.querySelector('[data-wyrd-bundle-check]')?.addEventListener('change', this.updateSummary);
      item.querySelector('[data-wyrd-bundle-variant]')?.addEventListener('change', this.updateSummary);
    });
    this.button?.addEventListener('click', this.submitBundle);
    this.updateSummary();
  }

  disconnectedCallback() {
    this.items.forEach((item) => {
      item.querySelector('[data-wyrd-bundle-check]')?.removeEventListener('change', this.updateSummary);
      item.querySelector('[data-wyrd-bundle-variant]')?.removeEventListener('change', this.updateSummary);
    });
    this.button?.removeEventListener('click', this.submitBundle);
  }

  selectedItems() {
    return this.items
      .map((item) => {
        const checkbox = item.querySelector('[data-wyrd-bundle-check]');
        const select = item.querySelector('[data-wyrd-bundle-variant]');
        if (!checkbox?.checked || !select) return null;
        const option = select.selectedOptions[0];
        return {
          id: Number(select.value),
          quantity: 1,
          price: Number(option?.dataset.price || 0),
          available: option?.dataset.available !== 'false',
        };
      })
      .filter(Boolean);
  }

  updateSummary() {
    let subtotal = 0;
    let selectedCount = 0;

    this.items.forEach((item) => {
      const checkbox = item.querySelector('[data-wyrd-bundle-check]');
      const select = item.querySelector('[data-wyrd-bundle-variant]');
      const priceTarget = item.querySelector('[data-wyrd-bundle-item-price]');
      const option = select?.selectedOptions[0];

      if (priceTarget && option?.dataset.priceFormatted) {
        priceTarget.textContent = option.dataset.priceFormatted;
      }

      const available = option?.dataset.available !== 'false';
      if (checkbox) checkbox.disabled = !available;
      if (!available && checkbox) checkbox.checked = false;

      if (checkbox?.checked && available) {
        selectedCount += 1;
        subtotal += Number(option?.dataset.price || 0);
      }
    });

    if (this.total) this.total.textContent = WyrdCommerce.moneyFromCents(subtotal, this.currency);
    if (this.count) this.count.textContent = `${selectedCount} item${selectedCount === 1 ? '' : 's'}`;
    if (this.button) this.button.disabled = selectedCount === 0 || this.processing;
  }

  setStatus(message, state = 'success') {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.state = state;
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.processing = isLoading;
    this.button.setAttribute('aria-busy', String(isLoading));
    this.button.textContent = isLoading ? 'Adding package...' : this.button.dataset.defaultLabel || 'Add selected kits';
    this.updateSummary();
  }

  async submitBundle() {
    if (this.processing) return;
    const selected = this.selectedItems();
    if (!selected.length) {
      this.setStatus('Choose at least one kit.', 'error');
      return;
    }
    if (selected.some((item) => !item.available)) {
      this.setStatus('One of the selected items is no longer available.', 'error');
      return;
    }

    this.setStatus('');
    this.setLoading(true);

    try {
      const response = await fetch(WyrdCommerce.cartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: selected.map(({ id, quantity }) => ({ id, quantity })),
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      });
      const responseJson = await WyrdCommerce.readJson(response);
      WyrdCommerce.updateAndOpenCart(responseJson);
      this.setStatus('Selected kits were added to the cart.');
      window.setTimeout(() => this.setStatus(''), 2800);
    } catch (error) {
      this.setStatus(error.message, 'error');
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('wyrd-bundle-builder')) {
  customElements.define('wyrd-bundle-builder', WyrdBundleBuilder);
}

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-wyrd-collection-jump]');
  if (!select || !select.value) return;
  window.location.assign(select.value);
});

/*
 * Flow renders product-page cross-sells inside an open shadow root. Its bundled
 * component uses an invalid flex direction and permits product names to break
 * between individual letters. Harden each instance after the component mounts
 * so the "Don't forget these..." cards remain readable at every viewport.
 */
const wyrdCrossSellStyles = `
  :host {
    display: grid !important;
    grid-template-columns: minmax(72px, 90px) minmax(0, 1fr) !important;
    align-items: start;
    width: 100%;
    min-width: 0;
  }

  .product-image {
    width: 100% !important;
    min-width: 0;
    padding-right: 15px;
  }

  .info,
  .product-info {
    min-width: 0;
  }

  .title,
  .title a,
  .product-title,
  .product-title a,
  [class*="title"],
  [class*="title"] a {
    word-break: normal !important;
    overflow-wrap: break-word !important;
    hyphens: none !important;
    white-space: normal !important;
  }
`;

function hardenProductCrossSells(root = document) {
  root.querySelectorAll?.('product-cross-sell').forEach((component) => {
    const shadowRoot = component.shadowRoot;
    if (!shadowRoot || shadowRoot.querySelector('[data-wyrd-cross-sell-fix]')) return;

    const style = document.createElement('style');
    style.dataset.wyrdCrossSellFix = '';
    style.textContent = wyrdCrossSellStyles;
    shadowRoot.append(style);
  });
}

customElements.whenDefined('product-cross-sell').then(() => {
  hardenProductCrossSells();

  const crossSellObserver = new MutationObserver(() => hardenProductCrossSells());
  crossSellObserver.observe(document.body, { childList: true, subtree: true });
});

/*
 * Flow may initialize a gallery on a variant-linked media item. Show the first
 * rendered product image initially on both desktop and mobile, exactly once per
 * gallery instance. Once this one-time initialization runs, Swiper and the
 * shopper retain complete control of thumbnail and swipe navigation.
 */
function initializePrimaryProductMedia(root = document) {
  root
    .querySelectorAll?.(
      '.template-product product-media[data-wyrd-initial-slide="first"]:not([data-wyrd-initial-slide-applied])'
    )
    .forEach((gallery) => {
      const main = gallery.querySelector('[data-media-main]');
      const mainSwiper = main?.swiper;
      if (!main?.classList.contains('swiper-initialized') || typeof mainSwiper?.slideTo !== 'function') {
        return;
      }

      mainSwiper.slideTo(0, 0, false);

      const thumbnails = gallery.querySelector('[data-media-thumbnails]');
      const thumbnailSwiper = thumbnails?.swiper;
      if (typeof thumbnailSwiper?.slideTo === 'function') {
        thumbnailSwiper.slideTo(0, 0, false);
      }

      gallery.setAttribute('data-wyrd-initial-slide-applied', 'true');
    });
}

let productMediaFrame = 0;
function schedulePrimaryProductMedia(root = document) {
  window.cancelAnimationFrame(productMediaFrame);
  productMediaFrame = window.requestAnimationFrame(() => initializePrimaryProductMedia(root));
}

schedulePrimaryProductMedia();

const productMediaObserver = new MutationObserver((mutations) => {
  const galleryChanged = mutations.some((mutation) => {
    if (mutation.type === 'attributes') {
      return mutation.target.matches?.(
        '.template-product product-media[data-wyrd-initial-slide="first"] [data-media-main]'
      );
    }

    return Array.from(mutation.addedNodes).some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.('.template-product product-media[data-wyrd-initial-slide="first"]') ||
          node.querySelector?.('.template-product product-media[data-wyrd-initial-slide="first"]'))
    );
  });

  if (galleryChanged) schedulePrimaryProductMedia();
});

productMediaObserver.observe(document.body, {
  attributes: true,
  attributeFilter: ['class'],
  childList: true,
  subtree: true,
});

/*
 * Swym shortens the product title in its remove-from-cart prompt before it is
 * inserted into the page. Preserve the full cart item title so the prompt has
 * enough context to make the choice useful.
 */
let lastRemovedCartProductTitle = '';

function removedCartProductTitle(button) {
  const ariaLabel = button?.getAttribute('aria-label') || '';
  if (/^remove\s+/i.test(ariaLabel)) {
    return ariaLabel.replace(/^remove\s*(?:-|:)?\s*/i, '').trim();
  }

  const cartItem = button?.closest(
    '.cart-drawer__product_card, .cart__item, [data-cart-item]'
  );
  return (
    cartItem
      ?.querySelector(
        '.cart-drawer__product_card--title, .cart__item-title, [data-cart-item-title]'
      )
      ?.textContent.trim() || ''
  );
}

document.addEventListener(
  'click',
  (event) => {
    const removeButton = event.target.closest?.(
      '[data-cart-remove-item], [data-cart-remove], [aria-label^="Remove"]'
    );
    if (!removeButton) return;

    const title = removedCartProductTitle(removeButton);
    if (title) lastRemovedCartProductTitle = title;
  },
  true
);

function repairSaveForLaterPrompt(root = document) {
  const popup = root.querySelector?.('.swym-sfl-popup');
  if (!popup) return;

  const heading = popup.querySelector('.swym-modal-heading');
  const modal = popup.querySelector('.swym-modal-content');
  if (heading && modal) {
    if (heading.id !== 'WyrdSaveForLaterHeading') {
      heading.id = 'WyrdSaveForLaterHeading';
    }
    if (modal.getAttribute('aria-labelledby') !== heading.id) {
      modal.setAttribute('aria-labelledby', heading.id);
    }
  }

  const productTitle = popup.querySelector('.swym-sfl-modal-product-title');
  if (
    productTitle &&
    lastRemovedCartProductTitle &&
    (productTitle.textContent.includes('...') ||
      productTitle.textContent.trim().length < lastRemovedCartProductTitle.length)
  ) {
    productTitle.textContent = lastRemovedCartProductTitle;
  }

  popup.querySelectorAll('.swym-sfl-popup-save-btn').forEach((button) => {
    if (button.textContent.trim() !== 'Save for later') {
      button.textContent = 'Save for later';
    }
  });
}

repairSaveForLaterPrompt();

const saveForLaterPromptObserver = new MutationObserver(() => repairSaveForLaterPrompt());
saveForLaterPromptObserver.observe(document.body, {
  childList: true,
  subtree: true,
});

const loyaltyLionStoreName = 'Wyrd Miniatures - Online Store';

function shortenLoyaltyLionStoreName(root = document) {
  root
    .querySelectorAll?.('#loyaltylion .lion-loyalty-splash-column__intro-text')
    .forEach((intro) => {
      const walker = document.createTreeWalker(intro, NodeFilter.SHOW_TEXT);
      let textNode = walker.nextNode();

      while (textNode) {
        if (textNode.nodeValue.includes(loyaltyLionStoreName)) {
          textNode.nodeValue = textNode.nodeValue.replaceAll(loyaltyLionStoreName, 'Wyrd');
        }
        textNode = walker.nextNode();
      }
    });
}

let loyaltyLionFrame = 0;
function scheduleLoyaltyLionStoreName() {
  window.cancelAnimationFrame(loyaltyLionFrame);
  loyaltyLionFrame = window.requestAnimationFrame(() => shortenLoyaltyLionStoreName());
}

scheduleLoyaltyLionStoreName();

const loyaltyLionObserver = new MutationObserver(() => scheduleLoyaltyLionStoreName());
loyaltyLionObserver.observe(document.body, {
  characterData: true,
  childList: true,
  subtree: true,
});
})();

/* assets/wyrd-storefront-repair-v28.js */
(() => {
const wyrdSaveForLaterPopupSelector = '.swym-sfl-popup';
const wyrdSaveForLaterActionSelector =
  '.swym-sfl-popup-action-container, .swym-action-btn-container';
const wyrdSaveForLaterButtonSelector =
  '.swym-sfl-popup-cancel, .swym-sfl-popup-save-btn';

function wyrdNormalizeSaveForLaterPopup(popup) {
  if (!(popup instanceof HTMLElement)) return;

  const candidates = Array.from(
    popup.querySelectorAll(wyrdSaveForLaterActionSelector)
  ).filter(
    (container) =>
      container.querySelectorAll(wyrdSaveForLaterButtonSelector).length >= 2
  );

  if (!candidates.length) return;

  const leafCandidates = candidates.filter(
    (container) =>
      !candidates.some(
        (other) => other !== container && container.contains(other)
      )
  );
  const primary =
    leafCandidates.find((container) =>
      container.classList.contains('swym-sfl-popup-action-container')
    ) ||
    leafCandidates.at(-1) ||
    candidates.at(-1);

  candidates.forEach((container) => {
    const isPrimary = container === primary;
    container.dataset.wyrdSflActions = isPrimary ? 'primary' : 'duplicate';
    container.toggleAttribute('hidden', !isPrimary);
    container.setAttribute('aria-hidden', String(!isPrimary));

    container
      .querySelectorAll(wyrdSaveForLaterButtonSelector)
      .forEach((button) => {
        if (isPrimary) {
          if (button.dataset.wyrdPreviousTabindex !== undefined) {
            const previousTabindex = button.dataset.wyrdPreviousTabindex;
            if (previousTabindex) {
              button.setAttribute('tabindex', previousTabindex);
            } else {
              button.removeAttribute('tabindex');
            }
            delete button.dataset.wyrdPreviousTabindex;
          }
          return;
        }

        if (button.dataset.wyrdPreviousTabindex === undefined) {
          button.dataset.wyrdPreviousTabindex =
            button.getAttribute('tabindex') || '';
        }
        button.setAttribute('tabindex', '-1');
      });
  });

  popup.dataset.wyrdSflNormalized = 'true';
}

function wyrdNormalizeSaveForLaterPopups(root = document) {
  if (root instanceof Element && root.matches(wyrdSaveForLaterPopupSelector)) {
    wyrdNormalizeSaveForLaterPopup(root);
  }

  root
    .querySelectorAll?.(wyrdSaveForLaterPopupSelector)
    .forEach(wyrdNormalizeSaveForLaterPopup);
}

let wyrdSaveForLaterRepairScheduled = false;

function wyrdScheduleSaveForLaterRepair() {
  if (wyrdSaveForLaterRepairScheduled) return;
  wyrdSaveForLaterRepairScheduled = true;

  window.requestAnimationFrame(() => {
    wyrdSaveForLaterRepairScheduled = false;
    wyrdNormalizeSaveForLaterPopups();
  });
}

wyrdNormalizeSaveForLaterPopups();

const wyrdSaveForLaterObserver = new MutationObserver(
  wyrdScheduleSaveForLaterRepair
);
wyrdSaveForLaterObserver.observe(document.body, {
  childList: true,
  subtree: true,
});
})();

/* assets/wyrd-mobile-interface-v30.js */
(() => {
const wyrdSwymGridSelector = [
  '.swym-wishlist-grid',
  '.swym-products-grid',
  '.swym-sfl-layout-grid',
  '.swym-sfl-list',
  '.swym-save-for-later-list',
  '.swym-saved-for-later-list'
].join(',');

const wyrdSwymCardSelector = [
  '.swym-wishlist-item',
  '.swym-sfl-card-product-item',
  '.swym-sfl-list-item',
  '.swym-save-for-later-item',
  '.swym-saved-for-later-item'
].join(',');

const wyrdSaveForLaterPopupSelector = '.swym-sfl-popup';

let wyrdSwymFrame = 0;

function wyrdMarkSaveForLaterOptOut(popup) {
  const labels = Array.from(popup.querySelectorAll('label'));
  const label = labels.find((candidate) =>
    /don.?t ask me again/i.test(candidate.textContent || '')
  );
  const checkbox =
    label?.control ||
    label?.querySelector('input[type="checkbox"]') ||
    (label?.htmlFor
      ? popup.querySelector(`#${CSS.escape(label.htmlFor)}`)
      : null) ||
    Array.from(popup.querySelectorAll('input[type="checkbox"]')).find(
      (candidate) =>
        /don.?t ask me again/i.test(
          candidate.parentElement?.textContent || ''
        )
    );

  if (!(label instanceof HTMLElement) || !(checkbox instanceof HTMLElement)) {
    return null;
  }

  checkbox.classList.add('wyrd-sfl-optout-checkbox');

  if (label.contains(checkbox)) {
    label.classList.remove('wyrd-sfl-optout-label');
    label.classList.add('wyrd-sfl-optout-row');
    return label;
  }

  let row = popup.querySelector('[data-wyrd-sfl-optout-row="true"]');
  if (!(row instanceof HTMLElement)) {
    row = document.createElement('div');
    row.dataset.wyrdSflOptoutRow = 'true';
    row.className = 'wyrd-sfl-optout-row';
  }

  label.classList.add('wyrd-sfl-optout-label');
  if (row.firstElementChild !== checkbox) {
    row.prepend(checkbox);
  }
  if (checkbox.nextElementSibling !== label) {
    row.append(label);
  }

  return row;
}

function wyrdBuildSaveForLaterFlow(popup, optOutRow) {
  const dialog =
    popup.querySelector('.swym-sfl-popup-dialog') ||
    popup.querySelector('.swym-modal-content');
  const heading = popup.querySelector('.swym-modal-heading');
  const productInfo = popup.querySelector('.swym-sfl-modal-product-info');
  const actions =
    popup.querySelector('[data-wyrd-sfl-actions="primary"]') ||
    popup.querySelector('.swym-sfl-popup-action-container');

  if (
    !(dialog instanceof HTMLElement) ||
    !(heading instanceof HTMLElement) ||
    !(productInfo instanceof HTMLElement) ||
    !(actions instanceof HTMLElement)
  ) {
    return;
  }

  let flow = dialog.querySelector(':scope > .wyrd-sfl-flow');
  if (!(flow instanceof HTMLElement)) {
    flow = document.createElement('div');
    flow.className = 'wyrd-sfl-flow';
    dialog.append(flow);
  }

  const desired = [heading, productInfo, optOutRow, actions].filter(
    (node) => node instanceof HTMLElement
  );
  const originalParents = new Set(
    desired
      .map((node) => node.parentElement)
      .filter((parent) => parent && parent !== flow && parent !== dialog)
  );

  desired.forEach((node, index) => {
    if (flow.children[index] !== node) {
      flow.insertBefore(node, flow.children[index] || null);
    }
  });

  originalParents.forEach((parent) => {
    const hasContent =
      (parent.textContent || '').trim() ||
      parent.querySelector('input, button, a, img, svg');
    parent.classList.toggle('wyrd-sfl-empty-shell', !hasContent);
  });
}

function wyrdNormalizeSwymContextMenus() {
  document
    .querySelectorAll(
      '.wyrd-swym-menu-ancestor, .wyrd-swym-menu-layer, .wyrd-swym-context-menu'
    )
    .forEach((element) => {
      element.classList.remove(
        'wyrd-swym-menu-ancestor',
        'wyrd-swym-menu-layer',
        'wyrd-swym-context-menu'
      );
    });

  document
    .querySelectorAll(
      '.swym-wishlist-context-menu, .swym-wishlist-context-menu-content, .swym-wishlist-selector-dropdown'
    )
    .forEach((menu) => {
      if (!(menu instanceof HTMLElement)) return;

      const style = window.getComputedStyle(menu);
      const isVisible =
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        menu.getClientRects().length > 0 &&
        menu.getAttribute('aria-hidden') !== 'true';

      if (!isVisible) return;

      menu.classList.add('wyrd-swym-context-menu');
      if (menu.parentElement) {
        menu.parentElement.classList.add('wyrd-swym-menu-layer');
      }

      let ancestor = menu.parentElement;
      while (ancestor && ancestor !== document.body) {
        ancestor.classList.add('wyrd-swym-menu-ancestor');
        if (ancestor.matches('.swym-ui-component')) break;
        ancestor = ancestor.parentElement;
      }
    });
}

function wyrdNormalizeSwymMobileCards() {
  wyrdSwymFrame = 0;

  document
    .querySelectorAll(wyrdSaveForLaterPopupSelector)
    .forEach((popup) => {
      popup.classList.add('wyrd-sfl-modal');
      const optOutRow = wyrdMarkSaveForLaterOptOut(popup);
      wyrdBuildSaveForLaterFlow(popup, optOutRow);
    });

  document.querySelectorAll(wyrdSwymGridSelector).forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;

    const cards = Array.from(grid.querySelectorAll(wyrdSwymCardSelector)).filter(
      (card) => card instanceof HTMLElement
    );

    if (!cards.length) return;

    grid.classList.add('wyrd-swym-mobile-grid');
    cards.forEach((card) => {
      card.classList.add('wyrd-swym-mobile-card');

      let slot = card;
      while (slot.parentElement && slot.parentElement !== grid) {
        slot = slot.parentElement;
      }
      if (slot.parentElement === grid) {
        slot.classList.add('wyrd-swym-mobile-slot');
      }
    });
  });

  wyrdNormalizeSwymContextMenus();
}

function wyrdScheduleSwymMobileCards() {
  if (wyrdSwymFrame) return;
  wyrdSwymFrame = requestAnimationFrame(wyrdNormalizeSwymMobileCards);
}

wyrdScheduleSwymMobileCards();

const wyrdSwymObserver = new MutationObserver(wyrdScheduleSwymMobileCards);
wyrdSwymObserver.observe(document.documentElement, {
  childList: true,
  subtree: true
});

window.addEventListener('pageshow', wyrdScheduleSwymMobileCards);
document.addEventListener(
  'click',
  () => window.setTimeout(wyrdScheduleSwymMobileCards, 0),
  true
);
})();

/* assets/wyrd-wishlist-repair-v31.js */
(() => {
const wyrdClearModalSelector = '.swym-clear-wishlist-modal';
const wyrdContextMenuSelector = [
  '.swym-wishlist-context-menu',
  '.swym-wishlist-context-menu-content',
  '.swym-wishlist-selector-dropdown'
].join(',');

let wyrdWishlistRepairFrame = 0;

function wyrdWishlistElementIsVisible(element) {
  if (!(element instanceof HTMLElement)) return false;

  const style = window.getComputedStyle(element);
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    element.getAttribute('aria-hidden') !== 'true' &&
    element.getClientRects().length > 0
  );
}

function wyrdNormalizeWishlistLayers() {
  wyrdWishlistRepairFrame = 0;

  document
    .querySelectorAll('.wyrd-swym-menu-clickable')
    .forEach((element) => {
      if (!wyrdWishlistElementIsVisible(element)) {
        element.classList.remove('wyrd-swym-menu-clickable');
      }
    });

  document.querySelectorAll('.wyrd-swym-clear-modal').forEach((modal) => {
    if (wyrdWishlistElementIsVisible(modal)) return;

    modal.classList.remove('wyrd-swym-clear-modal');
    modal
      .querySelectorAll('.wyrd-swym-clear-dialog')
      .forEach((dialog) => dialog.classList.remove('wyrd-swym-clear-dialog'));
  });

  document.querySelectorAll('.wyrd-swym-clear-ancestor').forEach((ancestor) => {
    if (!ancestor.querySelector('.wyrd-swym-clear-modal')) {
      ancestor.classList.remove('wyrd-swym-clear-ancestor');
    }
  });

  document.querySelectorAll(wyrdContextMenuSelector).forEach((menu) => {
    if (!wyrdWishlistElementIsVisible(menu)) return;
    menu.classList.add('wyrd-swym-menu-clickable');
  });

  document.querySelectorAll(wyrdClearModalSelector).forEach((modal) => {
    if (!wyrdWishlistElementIsVisible(modal)) return;

    modal.classList.add('wyrd-swym-clear-modal');

    const dialog = modal.querySelector(
      '.swym-clear-wishlist-modal-dialog, .swym-modal-content'
    );
    if (dialog instanceof HTMLElement) {
      dialog.classList.add('wyrd-swym-clear-dialog');
    }

    let ancestor = modal.parentElement;
    while (ancestor && ancestor !== document.body) {
      ancestor.classList.add('wyrd-swym-clear-ancestor');
      if (ancestor.matches('.swym-ui-component')) break;
      ancestor = ancestor.parentElement;
    }
  });
}

function wyrdScheduleWishlistRepair() {
  if (wyrdWishlistRepairFrame) return;
  wyrdWishlistRepairFrame = requestAnimationFrame(
    wyrdNormalizeWishlistLayers
  );
}

wyrdScheduleWishlistRepair();

const wyrdWishlistRepairObserver = new MutationObserver(
  wyrdScheduleWishlistRepair
);
wyrdWishlistRepairObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['aria-hidden', 'class', 'style'],
  childList: true,
  subtree: true
});

document.addEventListener(
  'click',
  () => {
    wyrdScheduleWishlistRepair();
    window.setTimeout(wyrdScheduleWishlistRepair, 60);
    window.setTimeout(wyrdScheduleWishlistRepair, 180);
  },
  true
);

window.addEventListener('pageshow', wyrdScheduleWishlistRepair);
})();

/* assets/wyrd-storefront-repair-v32.js */
(() => {
const wyrdSflOptOutPattern = /^don.?t ask me again[.!]?$/i;
let wyrdSflV32Frame = 0;

function wyrdSflText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function wyrdFindSflOptOutText(popup) {
  const matches = Array.from(
    popup.querySelectorAll('label, span, p, div')
  ).filter((element) =>
    wyrdSflOptOutPattern.test(wyrdSflText(element.textContent))
  );

  return (
    matches.find(
      (element) =>
        !Array.from(element.children).some((child) =>
          wyrdSflOptOutPattern.test(wyrdSflText(child.textContent))
        )
    ) || null
  );
}

function wyrdBuildSflOptOutRow(popup) {
  const textElement = wyrdFindSflOptOutText(popup);
  const relatedLabel =
    textElement instanceof HTMLLabelElement
      ? textElement
      : textElement?.closest('label');
  const checkbox =
    relatedLabel?.control ||
    relatedLabel?.querySelector('input[type="checkbox"]') ||
    popup.querySelector('input[type="checkbox"]');

  if (
    !(textElement instanceof HTMLElement) ||
    !(checkbox instanceof HTMLInputElement)
  ) {
    return null;
  }

  let row = relatedLabel;
  if (!(row instanceof HTMLLabelElement)) {
    row = document.createElement('label');
    textElement.before(row);
  }

  if (!checkbox.id) {
    checkbox.id = `WyrdSflOptOut-${Math.random().toString(36).slice(2, 9)}`;
  }

  row.htmlFor = checkbox.id;
  row.dataset.wyrdSflOptoutRowV32 = 'true';
  row.classList.add(
    'wyrd-sfl-optout-row',
    'wyrd-sfl-optout-row-v32'
  );
  row.classList.remove('wyrd-sfl-optout-label');

  checkbox.classList.add('wyrd-sfl-optout-checkbox');
  if (row.firstElementChild !== checkbox) {
    row.prepend(checkbox);
  }

  let copy = row.querySelector(':scope > .wyrd-sfl-optout-copy');
  if (!(copy instanceof HTMLElement)) {
    copy = document.createElement('span');
    copy.className = 'wyrd-sfl-optout-copy';

    if (textElement === row) {
      Array.from(row.childNodes)
        .filter((node) => node !== checkbox)
        .forEach((node) => copy.append(node));
    } else {
      copy.textContent = wyrdSflText(textElement.textContent);
      textElement.remove();
    }

    row.append(copy);
  }

  if (!wyrdSflOptOutPattern.test(wyrdSflText(copy.textContent))) {
    copy.textContent = 'Don’t ask me again';
  }

  return row;
}

function wyrdRepairSflPromptV32(popup) {
  if (!(popup instanceof HTMLElement)) return;

  popup.classList.add('wyrd-sfl-modal');

  const dialog =
    popup.querySelector('.swym-sfl-popup-dialog') ||
    popup.querySelector('.swym-modal-content');
  const heading = popup.querySelector('.swym-modal-heading');
  const productInfo = popup.querySelector('.swym-sfl-modal-product-info');
  const actions =
    popup.querySelector('[data-wyrd-sfl-actions="primary"]') ||
    popup.querySelector('.swym-sfl-popup-action-container');
  const optOutRow = wyrdBuildSflOptOutRow(popup);

  if (
    !(dialog instanceof HTMLElement) ||
    !(heading instanceof HTMLElement) ||
    !(productInfo instanceof HTMLElement) ||
    !(actions instanceof HTMLElement) ||
    !(optOutRow instanceof HTMLElement)
  ) {
    return;
  }

  let flow = dialog.querySelector(':scope > .wyrd-sfl-flow');
  if (!(flow instanceof HTMLElement)) {
    flow = document.createElement('div');
    flow.className = 'wyrd-sfl-flow';
    dialog.append(flow);
  }

  [heading, productInfo, optOutRow, actions].forEach((node, index) => {
    if (flow.children[index] !== node) {
      flow.insertBefore(node, flow.children[index] || null);
    }
  });
}

function wyrdRunSflPromptV32() {
  wyrdSflV32Frame = 0;
  document
    .querySelectorAll('.swym-sfl-popup')
    .forEach(wyrdRepairSflPromptV32);
}

function wyrdScheduleSflPromptV32() {
  if (wyrdSflV32Frame) return;
  wyrdSflV32Frame = requestAnimationFrame(wyrdRunSflPromptV32);
}

wyrdScheduleSflPromptV32();

new MutationObserver(wyrdScheduleSflPromptV32).observe(
  document.documentElement,
  {
    childList: true,
    subtree: true
  }
);

window.addEventListener('pageshow', wyrdScheduleSflPromptV32);
document.addEventListener('click', wyrdScheduleSflPromptV32, true);
})();

/* assets/wyrd-storefront-repair-v33.js */
(() => {
const wyrdSflOptOutPatternV33 = /^don.?t ask me again[.!]?$/i;
const wyrdEmptyWishlistPatternV33 = /^love it\?\s*add to my wishlist$/i;
const wyrdClearListPatternV33 = /^clear list[.!]?$/i;
let wyrdStorefrontV33Frame = 0;

function wyrdTextV33(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function wyrdVisibleV33(element) {
  return (
    element instanceof HTMLElement &&
    !element.hidden &&
    element.getClientRects().length > 0
  );
}

function wyrdLeafTextElementV33(root, pattern) {
  return (
    Array.from(root.querySelectorAll('label, span, p, div, h1, h2, h3'))
      .filter(
        (element) =>
          !element.matches('[data-wyrd-sfl-optout-source-v33="true"]') &&
          pattern.test(wyrdTextV33(element.textContent))
      )
      .find(
        (element) =>
          !Array.from(element.children).some((child) =>
            pattern.test(wyrdTextV33(child.textContent))
          )
      ) || null
  );
}

function wyrdFindActionsV33(popup, dialog) {
  const visibleButtons = Array.from(popup.querySelectorAll('button')).filter(
    wyrdVisibleV33
  );
  const decline = visibleButtons.find((button) =>
    /^no,?\s*thanks!?$/i.test(wyrdTextV33(button.textContent))
  );
  const save = visibleButtons.find((button) =>
    /^save for later$/i.test(wyrdTextV33(button.textContent))
  );

  if (!(decline instanceof HTMLButtonElement) ||
      !(save instanceof HTMLButtonElement)) {
    return (
      popup.querySelector('[data-wyrd-sfl-actions="primary"]') ||
      popup.querySelector('.swym-sfl-popup-action-container') ||
      popup.querySelector('.swym-action-btn-container')
    );
  }

  let common = decline.parentElement;
  while (
    common instanceof HTMLElement &&
    common !== dialog &&
    !common.contains(save)
  ) {
    common = common.parentElement;
  }

  if (
    common instanceof HTMLElement &&
    common !== dialog &&
    common !== popup
  ) {
    return common;
  }

  let actions = dialog.querySelector(':scope > .wyrd-sfl-actions-v33');
  if (!(actions instanceof HTMLElement)) {
    actions = document.createElement('div');
    dialog.append(actions);
  }
  actions.append(decline, save);
  return actions;
}

function wyrdBuildOptOutRowV33(popup) {
  const existing = popup.querySelector('.wyrd-sfl-optout-row-v33');
  if (existing instanceof HTMLLabelElement) return existing;

  const textElement = wyrdLeafTextElementV33(
    popup,
    wyrdSflOptOutPatternV33
  );
  const checkbox = popup.querySelector('input[type="checkbox"]');

  if (
    !(textElement instanceof HTMLElement) ||
    !(checkbox instanceof HTMLInputElement)
  ) {
    return null;
  }

  const oldLabel = checkbox.closest('label');
  const row = document.createElement('label');
  const copy = document.createElement('span');

  if (!checkbox.id) {
    checkbox.id = `WyrdSflOptOutV33-${Math.random()
      .toString(36)
      .slice(2, 9)}`;
  }

  row.htmlFor = checkbox.id;
  row.className = 'wyrd-sfl-optout-row-v33';
  copy.className = 'wyrd-sfl-optout-copy-v33';
  copy.textContent = 'Don’t ask me again';
  row.append(checkbox, copy);

  if (oldLabel instanceof HTMLLabelElement && oldLabel !== row) {
    oldLabel.dataset.wyrdSflOptoutSourceV33 = 'true';
  } else if (textElement !== row) {
    textElement.dataset.wyrdSflOptoutSourceV33 = 'true';
  }

  return row;
}

function wyrdRepairSflPromptV33(popup) {
  if (!(popup instanceof HTMLElement)) return;

  const dialog =
    popup.querySelector('.swym-sfl-popup-dialog') ||
    popup.querySelector('.swym-modal-content');
  const heading = popup.querySelector('.swym-modal-heading');
  const productInfo =
    popup.querySelector('.swym-sfl-modal-product-info') ||
    popup
      .querySelector('.swym-sfl-modal-product-title')
      ?.closest('.swym-sfl-modal-product-info, div');

  if (
    !(dialog instanceof HTMLElement) ||
    !(heading instanceof HTMLElement) ||
    !(productInfo instanceof HTMLElement)
  ) {
    return;
  }

  const optOutRow = wyrdBuildOptOutRowV33(popup);
  const actions = wyrdFindActionsV33(popup, dialog);
  if (
    !(optOutRow instanceof HTMLLabelElement) ||
    !(actions instanceof HTMLElement)
  ) {
    return;
  }

  popup.classList.add('wyrd-sfl-v33');
  let flow = dialog.querySelector(':scope > .wyrd-sfl-flow-v33');
  if (!(flow instanceof HTMLElement)) {
    flow = document.createElement('div');
    flow.className = 'wyrd-sfl-flow wyrd-sfl-flow-v33';
    dialog.append(flow);
  }

  actions.classList.add('wyrd-sfl-actions-v33');
  [heading, productInfo, optOutRow, actions].forEach((node, index) => {
    if (flow.children[index] !== node) {
      flow.insertBefore(node, flow.children[index] || null);
    }
  });
}

function wyrdRepairEmptyWishlistV33(root = document) {
  root
    .querySelectorAll('.wyrd-empty-wishlist-v33')
    .forEach((container) =>
      container.classList.remove('wyrd-empty-wishlist-v33')
    );
  root
    .querySelectorAll('.wyrd-empty-wishlist-stage-v33')
    .forEach((stage) =>
      stage.classList.remove('wyrd-empty-wishlist-stage-v33')
    );

  Array.from(root.querySelectorAll('h1, h2, h3, .swym-empty-wishlist-title'))
    .filter((heading) =>
      wyrdEmptyWishlistPatternV33.test(wyrdTextV33(heading.textContent))
    )
    .forEach((heading) => {
      const container =
        heading.closest('.swym-empty-wishlist-container') ||
        heading.parentElement;
      if (!(container instanceof HTMLElement)) return;

      container.classList.add('wyrd-empty-wishlist-v33');
      const stage =
        container.closest(
          '.swym-wishlist-detail-content, .swym-wishlist-detail, .swym-wishlist-container-content'
        ) || container.parentElement;
      if (stage instanceof HTMLElement) {
        stage.classList.add('wyrd-empty-wishlist-stage-v33');
      }
    });
}

function wyrdMarkCartNoteSaveV33(root = document) {
  root.querySelectorAll('cart-update-modal').forEach((modal) => {
    if (!modal.querySelector('#CartNote')) return;

    const saveButton = modal.querySelector(
      'button[data-cart-update-close].cart-drawer__note--close-btn'
    );
    if (saveButton instanceof HTMLButtonElement) {
      saveButton.classList.add('wyrd-cart-note-save');
    }
  });
}

function wyrdMarkWishlistRemoveButtonsV33(root = document) {
  const cardSelector = [
    '.swym-wishlist-item',
    '.swym-sfl-card-product-item',
    '.swym-sfl-list-item',
    '.swym-save-for-later-item',
    '.swym-saved-for-later-item',
    '[class*="wishlist-item"]',
    '[class*="save-for-later-item"]',
    '[class*="saved-for-later-item"]'
  ].join(',');

  root.querySelectorAll(cardSelector).forEach((card) => {
    card.querySelectorAll('button, a, [role="button"]').forEach((control) => {
      const fingerprint = [
        control.className,
        control.id,
        control.getAttribute('aria-label'),
        control.getAttribute('title')
      ]
        .map(wyrdTextV33)
        .join(' ');

      if (
        /(remove[-_\s]?(?:from[-_\s]?)?(?:wishlist|list|product)?|delete)/i.test(
          fingerprint
        ) &&
        !/(clear|close[-_\s]?(?:modal|dialog|drawer|wishlist))/i.test(
          fingerprint
        )
      ) {
        control.classList.add('wyrd-wishlist-remove-v33');
      }
    });
  });
}

function wyrdMarkClearListLayersV33(root = document) {
  Array.from(
    root.querySelectorAll(
      'button, a, [role="button"], [role="menuitem"], li, span, div'
    )
  )
    .filter((element) =>
      wyrdClearListPatternV33.test(wyrdTextV33(element.textContent))
    )
    .filter(
      (element) =>
        !Array.from(element.children).some((child) =>
          wyrdClearListPatternV33.test(wyrdTextV33(child.textContent))
        )
    )
    .forEach((leaf) => {
      const action =
        leaf.closest(
          'button, a, [role="button"], [role="menuitem"], li, [tabindex]'
        ) || leaf;
      const menu =
        action.closest(
          '.swym-wishlist-context-menu, .swym-wishlist-context-menu-content, .swym-wishlist-selector-dropdown, [role="menu"]'
        ) || action.parentElement;

      action.classList.add('wyrd-clear-list-action-v33');
      if (menu instanceof HTMLElement) {
        menu.classList.add('wyrd-clear-list-menu-v33');
      }
    });

  root
    .querySelectorAll(
      '.swym-clear-wishlist-modal, [role="dialog"], [class*="clear"][class*="modal"]'
    )
    .forEach((candidate) => {
      if (
        !(candidate instanceof HTMLElement) ||
        !/(clear|remove).{0,36}(wishlist|list)|wishlist.{0,36}(clear|remove)/i.test(
          wyrdTextV33(candidate.textContent)
        )
      ) {
        return;
      }

      const rootElement =
        candidate.closest('.swym-clear-wishlist-modal') || candidate;
      const dialog =
        rootElement.querySelector(
          '.swym-clear-wishlist-modal-dialog, .swym-modal-content, [role="dialog"]'
        ) || candidate;

      rootElement.classList.add('wyrd-clear-confirm-root-v33');
      if (dialog instanceof HTMLElement) {
        dialog.classList.add('wyrd-clear-confirm-dialog-v33');
      }
    });
}

function wyrdRunStorefrontV33() {
  wyrdStorefrontV33Frame = 0;
  wyrdMarkCartNoteSaveV33();
  wyrdMarkWishlistRemoveButtonsV33();
  wyrdMarkClearListLayersV33();
  document
    .querySelectorAll('.swym-sfl-popup')
    .forEach(wyrdRepairSflPromptV33);
  wyrdRepairEmptyWishlistV33();
}

function wyrdScheduleStorefrontV33() {
  if (wyrdStorefrontV33Frame) return;
  wyrdStorefrontV33Frame = requestAnimationFrame(wyrdRunStorefrontV33);
}

wyrdScheduleStorefrontV33();

new MutationObserver(wyrdScheduleStorefrontV33).observe(
  document.documentElement,
  {
    childList: true,
    subtree: true
  }
);

window.addEventListener('pageshow', wyrdScheduleStorefrontV33);
document.addEventListener('click', wyrdScheduleStorefrontV33, true);
})();

/* assets/wyrd-loyalty-help.js */
(() => {
const normalize = (value) => value.replace(/\s+/g, " ").trim();

const helpAnswers = [
  {
    question: "What is this?",
    current: "A loyalty program to say thank you for choosing to make your purchase with us!",
    replacement:
      "Wyrd Loyalty thanks customers with points for eligible purchases and other activities. Sign in to see your balance, current earning options, and available rewards."
  },
  {
    question: "How do I earn Loyalty Points?",
    current:
      "You can earn points by creating an account, visiting the website, and/or making purchases. Select the tab Earn Loyalty Points for more info.",
    replacement:
      "Open Earn points to see every activity currently available and the points each one awards. Available activities can change, so the list in your account is always the best source."
  },
  {
    question: "How do I view my point balance?",
    current: "Your point balance is listed in the bottom right corner of every page.",
    replacement:
      "Open Wyrd Loyalty and sign in with the same email address you use for orders. Your current point balance appears at the top of the panel."
  },
  {
    question: "How do I redeem my Loyalty Points?",
    current:
      "Select the tab called Get Rewards. If you have enough points, you can redeem them for a coupon.",
    replacement:
      "Open Redeem rewards, choose an available reward, and redeem your points to create a discount code. Copy that code and apply it during checkout."
  },
  {
    question: "Can I use my Loyalty Points during checkout?",
    current:
      "Not directly - please redeem your Loyalty Points for a coupon which can then be applied during checkout.",
    replacement:
      "Points are not spent directly at checkout. Redeem a reward first, then enter the generated discount code at checkout. Some offers and discounts cannot be combined."
  },
  {
    question: "Can I redeem my points for a physical item without placing an order?",
    current:
      "No, if you want tokens, pins, or other physical rewards, they must be redeemed with an order.",
    replacement:
      "No. Physical rewards must be redeemed with an order so inventory, shipping, and delivery details can be handled correctly."
  },
  {
    question: "Where can I report a problem or give feedback?",
    current: "Please use the CONTACT US form.",
    replacement:
      "Use the Contact Us form and include the email on your Wyrd account, the order number when relevant, the reward or discount code involved, and a screenshot if possible."
  }
];

const labelUpdates = new Map([
  ["Earn Loyalty Points", "Earn points"],
  ["Get rewards", "Redeem rewards"],
  ["Follow us on Twitter", "Follow us on X"],
  ["Visit", "Visit the Wyrd store"]
]);

function replaceExactText(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  nodes.forEach((node) => {
    const replacement = labelUpdates.get(normalize(node.nodeValue || ""));
    if (replacement) node.nodeValue = replacement;
  });
}

function updateHelp(root) {
  const page = root.querySelector(".lion-loyalty-panel-page-help");
  if (!page) return;

  const headings = [...page.querySelectorAll("h1, h2, h3, h4, h5, h6")];
  helpAnswers.forEach(({ question, current, replacement }) => {
    const heading = headings.find((element) => normalize(element.textContent || "") === question);
    if (!heading) return;

    let answer = heading.nextElementSibling;
    while (answer && normalize(answer.textContent || "") === "") {
      answer = answer.nextElementSibling;
    }

    if (answer && normalize(answer.textContent || "") === normalize(current)) {
      answer.textContent = replacement;
    }
  });
}

function refresh(root) {
  updateHelp(root);
  replaceExactText(root);
}

function watchLoyaltyLion() {
  const root = document.querySelector("#loyaltylion");
  if (!root) return false;

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      refresh(root);
    });
  };

  refresh(root);
  new MutationObserver(schedule).observe(root, {
    childList: true,
    subtree: true,
    characterData: true
  });
  return true;
}

if (!watchLoyaltyLion()) {
  const bootstrapObserver = new MutationObserver(() => {
    if (watchLoyaltyLion()) bootstrapObserver.disconnect();
  });
  bootstrapObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}
})();

/* assets/wyrd-feedback-repair-v38.js */
(() => {
const wyrdWishlistButtonSelectorV38 =
  '.wyrd-card-wishlist, .wyrd-product-wishlist';
const wyrdWishlistUninitializedButtonSelectorV38 =
  '.wyrd-card-wishlist:not(.swym-loaded), .wyrd-product-wishlist:not(.swym-loaded)';
const wyrdWishlistDrawerRootSelectorV38 =
  '.swym-storefront-layout-root-component, #swym-storefront-layout-container, #swym-storefront-extention-render-container';

let wyrdSwatV38 = null;
let wyrdWishlistRefreshTimerV38 = 0;
let wyrdWishlistMutationFrameV38 = 0;
let wyrdWishlistInitializationFrameV38 = 0;
let wyrdLoyaltyRepairFrameV38 = 0;
let wyrdWishlistDrawerFrameV38 = 0;
const wyrdPendingWishlistRemovalsV38 = new Map();
const wyrdPendingWishlistAdditionsV38 = new Map();

function wyrdWishlistProductV38(button) {
  return {
    epi: button.dataset.epi || button.dataset.variantId,
    empi: button.dataset.empi || button.dataset.productId,
    du: button.dataset.du || button.dataset.productUrl
  };
}

function wyrdWishlistItemMatchesV38(button, item) {
  const product = wyrdWishlistProductV38(button);
  const sameProduct = String(item.empi) === String(product.empi);

  if (button.classList.contains('wyrd-card-wishlist')) {
    return sameProduct;
  }

  return sameProduct && String(item.epi) === String(product.epi);
}

function wyrdWishlistPendingKeyV38(button) {
  const product = wyrdWishlistProductV38(button);

  if (button.classList.contains('wyrd-card-wishlist')) {
    return `product:${product.empi}`;
  }

  return `variant:${product.empi}:${product.epi}`;
}

function wyrdWishlistEventProductV38(event) {
  return event?.detail?.d || event?.detail || {};
}

function wyrdPendingKeyMatchesItemV38(key, item) {
  const [type, empi, epi] = key.split(':');
  if (String(item.empi) !== String(empi)) return false;
  return type === 'product' || String(item.epi) === String(epi);
}

function wyrdReconcilePendingRemovalsV38(lists) {
  const normalizedLists = Array.isArray(lists) ? lists : [];
  const now = Date.now();

  wyrdPendingWishlistRemovalsV38.forEach((record, key) => {
    const stillPresent = normalizedLists.some((list) =>
      (Array.isArray(list.listcontents) ? list.listcontents : []).some((item) =>
        wyrdPendingKeyMatchesItemV38(key, item)
      )
    );

    if (!stillPresent || record.expiresAt <= now) {
      wyrdPendingWishlistRemovalsV38.delete(key);
    }
  });
}

function wyrdReconcilePendingAdditionsV38(lists) {
  const normalizedLists = Array.isArray(lists) ? lists : [];
  const now = Date.now();

  wyrdPendingWishlistAdditionsV38.forEach((record, key) => {
    const isPresent = normalizedLists.some((list) =>
      (Array.isArray(list.listcontents) ? list.listcontents : []).some((item) =>
        wyrdPendingKeyMatchesItemV38(key, item)
      )
    );

    if (isPresent || record.expiresAt <= now) {
      wyrdPendingWishlistAdditionsV38.delete(key);
    }
  });
}

function wyrdWishlistItemIsPendingV38(item) {
  return Array.from(wyrdPendingWishlistRemovalsV38.keys()).some((key) =>
    wyrdPendingKeyMatchesItemV38(key, item)
  );
}

function wyrdEffectiveWishlistListsV38(lists) {
  return (Array.isArray(lists) ? lists : []).map((list) => ({
    ...list,
    listcontents: (Array.isArray(list.listcontents) ? list.listcontents : []).filter(
      (item) => !wyrdWishlistItemIsPendingV38(item)
    )
  }));
}

function wyrdWishlistButtonIsPendingAdditionV38(button) {
  const key = wyrdWishlistPendingKeyV38(button);

  if (wyrdPendingWishlistAdditionsV38.has(key)) return true;

  if (!button.classList.contains('wyrd-card-wishlist')) return false;

  const product = wyrdWishlistProductV38(button);
  return Array.from(wyrdPendingWishlistAdditionsV38.keys()).some((pendingKey) =>
    pendingKey.startsWith(`variant:${product.empi}:`)
  );
}

function wyrdSetWishlistButtonStateV38(button, isAdded) {
  const fullLabel = isAdded
    ? button.dataset.wyrdWishlistRemoveLabel
    : button.dataset.wyrdWishlistAddLabel;
  const shortLabel = isAdded
    ? button.dataset.wyrdWishlistRemoveShort
    : button.dataset.wyrdWishlistAddShort;

  button.classList.toggle('swym-added', isAdded);
  button.setAttribute('aria-pressed', isAdded ? 'true' : 'false');

  if (fullLabel) {
    button.setAttribute('aria-label', fullLabel);
    const hiddenLabel = button.querySelector('.visually-hidden');
    if (hiddenLabel) hiddenLabel.textContent = fullLabel;
  }

  if (shortLabel) {
    button.setAttribute('title', shortLabel);
    const tooltip = button.querySelector('.wyrd-wishlist-tooltip');
    if (tooltip) tooltip.textContent = shortLabel;
  }
}

function wyrdApplyWishlistListsV38(lists) {
  const normalizedLists = Array.isArray(lists) ? lists : [];
  wyrdReconcilePendingRemovalsV38(normalizedLists);
  wyrdReconcilePendingAdditionsV38(normalizedLists);
  const effectiveLists = wyrdEffectiveWishlistListsV38(normalizedLists);

  document.querySelectorAll(wyrdWishlistButtonSelectorV38).forEach((button) => {
    const isAdded =
      wyrdWishlistButtonIsPendingAdditionV38(button) ||
      effectiveLists.some((list) =>
        (Array.isArray(list.listcontents) ? list.listcontents : []).some((item) =>
          wyrdWishlistItemMatchesV38(button, item)
        )
      );

    wyrdSetWishlistButtonStateV38(button, isAdded);
  });

  if (
    wyrdPendingWishlistRemovalsV38.size ||
    wyrdPendingWishlistAdditionsV38.size
  ) {
    wyrdScheduleWishlistRefreshV38(450);
  }
}

function wyrdFetchWishlistListsV38() {
  return new Promise((resolve, reject) => {
    if (!wyrdSwatV38 || typeof wyrdSwatV38.fetchLists !== 'function') {
      resolve([]);
      return;
    }

    wyrdSwatV38.fetchLists({
      callbackFn: resolve,
      errorFn: reject
    });
  });
}

async function wyrdRefreshWishlistStateV38() {
  if (!wyrdSwatV38) return;

  try {
    const lists = await wyrdFetchWishlistListsV38();
    wyrdApplyWishlistListsV38(lists);
  } catch (error) {
    console.warn('Wyrd Wishlist state refresh failed.', error);
  }
}

function wyrdScheduleWishlistRefreshV38(delay = 0) {
  window.clearTimeout(wyrdWishlistRefreshTimerV38);
  wyrdWishlistRefreshTimerV38 = window.setTimeout(
    wyrdRefreshWishlistStateV38,
    delay
  );
}

function wyrdMatchedWishlistEntriesV38(button, lists) {
  const matches = [];

  (Array.isArray(lists) ? lists : []).forEach((list) => {
    (Array.isArray(list.listcontents) ? list.listcontents : []).forEach((item) => {
      if (wyrdWishlistItemMatchesV38(button, item)) {
        matches.push({ listId: list.lid, item });
      }
    });
  });

  return matches;
}

function wyrdDeleteWishlistEntryV38(entry, fallbackProduct) {
  return new Promise((resolve, reject) => {
    const product = {
      epi: entry.item.epi || fallbackProduct.epi,
      empi: entry.item.empi || fallbackProduct.empi,
      du: entry.item.du || fallbackProduct.du
    };

    wyrdSwatV38.deleteFromList(entry.listId, product, resolve, reject);
  });
}

function wyrdAddWishlistEntryV38(listId, product) {
  return new Promise((resolve, reject) => {
    wyrdSwatV38.addToList(listId, product, resolve, reject);
  });
}

function wyrdCreateWishlistV38() {
  return new Promise((resolve, reject) => {
    const name = document.body?.dataset.wyrdWishlistListName;
    if (!name) {
      reject(new Error('Wishlist list name is unavailable.'));
      return;
    }

    wyrdSwatV38.createList({ lname: name }, resolve, reject);
  });
}

async function wyrdResolveWishlistListIdV38(lists) {
  const normalizedLists = Array.isArray(lists) ? lists : [];
  const preferredListId = window.swymSelectedListId;
  const preferredList = normalizedLists.find(
    (list) => String(list.lid) === String(preferredListId)
  );

  if (preferredList?.lid) return preferredList.lid;
  if (normalizedLists[0]?.lid) return normalizedLists[0].lid;

  if (typeof wyrdSwatV38?.createList !== 'function') return null;

  const createdList = await wyrdCreateWishlistV38();
  return createdList?.lid || null;
}

function wyrdMarkPendingAdditionV38(button) {
  const key = wyrdWishlistPendingKeyV38(button);
  wyrdPendingWishlistRemovalsV38.delete(key);
  wyrdPendingWishlistAdditionsV38.set(key, {
    expiresAt: Date.now() + 6000
  });
}

function wyrdMarkPendingRemovalV38(button) {
  const key = wyrdWishlistPendingKeyV38(button);
  wyrdPendingWishlistAdditionsV38.delete(key);
  wyrdPendingWishlistRemovalsV38.set(key, {
    expiresAt: Date.now() + 6000
  });
}

function wyrdMarkEventRemovalV38(event) {
  const product = wyrdWishlistEventProductV38(event);
  if (!product.empi) return;

  const productKey = `product:${product.empi}`;
  wyrdPendingWishlistAdditionsV38.delete(productKey);
  wyrdPendingWishlistRemovalsV38.set(productKey, {
    expiresAt: Date.now() + 6000
  });

  if (product.epi) {
    const variantKey = `variant:${product.empi}:${product.epi}`;
    wyrdPendingWishlistAdditionsV38.delete(variantKey);
    wyrdPendingWishlistRemovalsV38.set(variantKey, {
      expiresAt: Date.now() + 6000
    });
  }
}

function wyrdClearPendingAdditionV38(event) {
  const product = wyrdWishlistEventProductV38(event);
  if (!product.empi) return;

  wyrdPendingWishlistRemovalsV38.delete(`product:${product.empi}`);
  wyrdPendingWishlistAdditionsV38.set(`product:${product.empi}`, {
    expiresAt: Date.now() + 6000
  });
  if (product.epi) {
    wyrdPendingWishlistRemovalsV38.delete(
      `variant:${product.empi}:${product.epi}`
    );
    wyrdPendingWishlistAdditionsV38.set(
      `variant:${product.empi}:${product.epi}`,
      {
        expiresAt: Date.now() + 6000
      }
    );
  }
}

async function wyrdAddWishlistButtonV38(button) {
  if (
    !wyrdSwatV38 ||
    typeof wyrdSwatV38.addToList !== 'function' ||
    button.dataset.wyrdWishlistBusy === 'true'
  ) {
    return;
  }

  const product = wyrdWishlistProductV38(button);
  if (!product.empi || !product.epi || !product.du) return;

  button.dataset.wyrdWishlistBusy = 'true';
  button.setAttribute('aria-busy', 'true');

  try {
    const lists = await wyrdFetchWishlistListsV38();
    const listId = await wyrdResolveWishlistListIdV38(lists);
    if (!listId) throw new Error('No wishlist is available.');

    await wyrdAddWishlistEntryV38(listId, product);
    wyrdMarkPendingAdditionV38(button);
    wyrdSetWishlistButtonStateV38(button, true);
    wyrdScheduleWishlistRefreshV38(350);
  } catch (error) {
    wyrdPendingWishlistAdditionsV38.delete(
      wyrdWishlistPendingKeyV38(button)
    );
    wyrdSetWishlistButtonStateV38(button, false);
    console.warn('Wyrd Wishlist addition failed.', error);
  } finally {
    delete button.dataset.wyrdWishlistBusy;
    button.removeAttribute('aria-busy');
  }
}

async function wyrdRemoveWishlistButtonV38(button) {
  if (
    !wyrdSwatV38 ||
    typeof wyrdSwatV38.deleteFromList !== 'function' ||
    button.dataset.wyrdWishlistBusy === 'true'
  ) {
    return;
  }

  button.dataset.wyrdWishlistBusy = 'true';
  button.setAttribute('aria-busy', 'true');

  try {
    const lists = await wyrdFetchWishlistListsV38();
    const matches = wyrdMatchedWishlistEntriesV38(button, lists);
    const fallbackProduct = wyrdWishlistProductV38(button);

    if (matches.length) {
      await Promise.all(
        matches.map((entry) =>
          wyrdDeleteWishlistEntryV38(entry, fallbackProduct)
        )
      );
    }

    wyrdMarkPendingRemovalV38(button);
    wyrdApplyWishlistListsV38(lists);
    wyrdScheduleWishlistRefreshV38(350);
  } catch (error) {
    wyrdPendingWishlistRemovalsV38.delete(wyrdWishlistPendingKeyV38(button));
    console.warn('Wyrd Wishlist removal failed.', error);
    wyrdScheduleWishlistRefreshV38(100);
  } finally {
    delete button.dataset.wyrdWishlistBusy;
    button.removeAttribute('aria-busy');
  }
}

function wyrdInitializeWishlistButtonsV38() {
  wyrdWishlistInitializationFrameV38 = 0;

  if (
    !wyrdSwatV38 ||
    typeof wyrdSwatV38.initializeActionButtons !== 'function' ||
    !document.querySelector(wyrdWishlistUninitializedButtonSelectorV38)
  ) {
    return;
  }

  wyrdSwatV38.initializeActionButtons(
    'body',
    wyrdWishlistUninitializedButtonSelectorV38
  );
}

function wyrdScheduleWishlistButtonInitializationV38() {
  if (wyrdWishlistInitializationFrameV38) return;

  wyrdWishlistInitializationFrameV38 = window.requestAnimationFrame(
    wyrdInitializeWishlistButtonsV38
  );
}

function wyrdInitWishlistV38(swat) {
  if (!swat || typeof swat.fetchLists !== 'function') return;
  if (wyrdSwatV38 === swat) {
    wyrdScheduleWishlistButtonInitializationV38();
    wyrdScheduleWishlistRefreshV38();
    return;
  }

  wyrdSwatV38 = swat;
  wyrdScheduleWishlistButtonInitializationV38();
  wyrdScheduleWishlistRefreshV38();

  const addedEvent = swat.JSEvents && swat.JSEvents.addedToWishlist;
  const removedEvent = swat.JSEvents && swat.JSEvents.removedFromWishlist;

  if (swat.evtLayer && typeof swat.evtLayer.addEventListener === 'function') {
    if (addedEvent) {
      swat.evtLayer.addEventListener(addedEvent, (event) => {
        wyrdClearPendingAdditionV38(event);
        wyrdScheduleWishlistRefreshV38(80);
      });
    }
    if (removedEvent) {
      swat.evtLayer.addEventListener(removedEvent, (event) => {
        wyrdMarkEventRemovalV38(event);
        wyrdScheduleWishlistRefreshV38(120);
      });
    }
  }
}

function wyrdWishlistDrawerHashIsOpenV38() {
  const currentRoute =
    `${window.location.pathname}${window.location.hash}`.toLowerCase();
  if (!currentRoute) return false;

  const knownUrls = Object.values(
    window.SwymStorefrontLayoutContext?.StorefrontLayoutUrls || {}
  )
    .filter((value) => typeof value === 'string')
    .map((value) => value.toLowerCase());

  return (
    knownUrls.some(
      (value) =>
        currentRoute === value ||
        currentRoute.endsWith(value) ||
        currentRoute.endsWith(`#${value}`)
    ) ||
    /wishlist|saved-for-later|swym/.test(currentRoute)
  );
}

function wyrdWishlistElementIsVisibleV38(element) {
  if (!(element instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(element);

  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    element.getAttribute('aria-hidden') !== 'true' &&
    element.getClientRects().length > 0
  );
}

function wyrdWishlistElementIntersectsViewportV38(element) {
  if (!wyrdWishlistElementIsVisibleV38(element)) return false;

  const rect = element.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.right > 0 &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.top < window.innerHeight
  );
}

function wyrdWishlistDrawerRootsV38() {
  return Array.from(
    document.querySelectorAll(wyrdWishlistDrawerRootSelectorV38)
  );
}

function wyrdWishlistDrawerIsOpenV38() {
  if (wyrdWishlistDrawerHashIsOpenV38()) return true;

  return wyrdWishlistDrawerRootsV38().some((root) => {
    if (!wyrdWishlistElementIntersectsViewportV38(root)) return false;

    const panelText = (root.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    return (
      /open|active|visible/i.test(root.className) ||
      panelText.includes('my wishlist') ||
      panelText.includes('saved for later')
    );
  });
}

function wyrdWishlistTitleElementsV38() {
  const elements = [];

  wyrdWishlistDrawerRootsV38().forEach((root) => {
    root
      .querySelectorAll(
        'h1, h2, h3, h4, [role="heading"], button, [class*="title"], [class*="name"]'
      )
      .forEach((element) => {
        const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
        if (text.toLowerCase() === 'my wishlist') elements.push(element);
      });
  });

  return elements;
}

function wyrdEnsureWishlistDrawerCloseV38() {
  const closeLabel = document.body?.dataset.wyrdWishlistClose;
  if (!closeLabel) return;

  let button = document.querySelector('.wyrd-wishlist-drawer-close');

  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'wyrd-wishlist-drawer-close';
    button.setAttribute('aria-label', closeLabel);
    button.innerHTML =
      '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg><span></span>';
    button.querySelector('span').textContent = closeLabel;
    document.body.appendChild(button);
  }

  button.hidden = !wyrdWishlistDrawerIsOpenV38();
}

function wyrdRepairWishlistDrawerV38() {
  wyrdWishlistDrawerFrameV38 = 0;

  wyrdWishlistTitleElementsV38().forEach((element) => {
    element.classList.add('wyrd-wishlist-title-safe');
    element.parentElement?.classList.add('wyrd-wishlist-title-row-safe');
  });

  wyrdEnsureWishlistDrawerCloseV38();
}

function wyrdScheduleWishlistDrawerRepairV38() {
  if (wyrdWishlistDrawerFrameV38) return;

  wyrdWishlistDrawerFrameV38 = window.requestAnimationFrame(
    wyrdRepairWishlistDrawerV38
  );
}

function wyrdFindNativeWishlistCloseV38() {
  const selectors = [
    'button[aria-label*="close" i]',
    'button[title*="close" i]',
    'button[class*="close" i]',
    '[role="button"][aria-label*="close" i]',
    '[data-testid*="close" i]',
    '[data-action*="close" i]'
  ].join(',');

  for (const root of wyrdWishlistDrawerRootsV38()) {
    const candidates = Array.from(root.querySelectorAll(selectors));
    const match = candidates.find(
      (candidate) =>
        !candidate.classList.contains('wyrd-wishlist-drawer-close') &&
        wyrdWishlistElementIsVisibleV38(candidate)
    );

    if (match) return match;
  }

  return null;
}

function wyrdFindWishlistBackdropV38() {
  const selectors = [
    '[class*="backdrop" i]',
    '[class*="overlay" i]',
    '[class*="modal-bg" i]'
  ].join(',');

  for (const root of wyrdWishlistDrawerRootsV38()) {
    const scope = root.parentElement || root;
    const candidates = Array.from(scope.querySelectorAll(selectors));
    const match = candidates.find(
      (candidate) =>
        candidate !== root &&
        !candidate.contains(root) &&
        wyrdWishlistElementIntersectsViewportV38(candidate)
    );

    if (match) return match;
  }

  return null;
}

function wyrdClearWishlistDrawerHashV38() {
  if (!window.location.hash) return;

  const oldURL = window.location.href;
  const cleanURL = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(window.history.state, document.title, cleanURL);
  const newURL = window.location.href;

  window.dispatchEvent(
    new HashChangeEvent('hashchange', {
      oldURL,
      newURL
    })
  );
  window.dispatchEvent(
    new PopStateEvent('popstate', {
      state: window.history.state
    })
  );
}

function wyrdCloseWishlistDrawerV38(event) {
  event.preventDefault();

  const nativeClose = wyrdFindNativeWishlistCloseV38();
  const backdrop = nativeClose ? null : wyrdFindWishlistBackdropV38();

  if (nativeClose) {
    nativeClose.click();
  } else if (backdrop instanceof HTMLElement) {
    backdrop.click();
  }

  window.setTimeout(() => {
    if (wyrdWishlistDrawerIsOpenV38()) {
      wyrdClearWishlistDrawerHashV38();
    }

    wyrdScheduleWishlistDrawerRepairV38();

    const launcher = Array.from(
      document.querySelectorAll('[aria-label="My Wishlist"], #swym-anchor')
    ).find(wyrdWishlistElementIsVisibleV38);

    if (launcher instanceof HTMLElement) launcher.focus();
  });
}

window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push(wyrdInitWishlistV38);

if (window._swat && typeof window._swat.fetchLists === 'function') {
  wyrdInitWishlistV38(window._swat);
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    const button =
      target instanceof Element
        ? target.closest(wyrdWishlistButtonSelectorV38)
        : null;

    if (!button) return;

    const isAdded =
      button.classList.contains('swym-added') ||
      button.getAttribute('aria-pressed') === 'true';

    event.preventDefault();
    event.stopImmediatePropagation();

    if (isAdded) {
      wyrdRemoveWishlistButtonV38(button);
    } else {
      wyrdAddWishlistButtonV38(button);
    }
  },
  true
);

document.addEventListener('click', (event) => {
  const target = event.target;
  const closeButton =
    target instanceof Element
      ? target.closest('cart-drawer [data-wyrd-cart-close]')
      : null;

  if (!closeButton) return;

  const drawer = closeButton.closest('cart-drawer');
  if (!drawer || typeof drawer.closeDrawer !== 'function') return;

  event.preventDefault();
  drawer.closeDrawer(event);
});

document.addEventListener('click', (event) => {
  const target = event.target;
  const closeButton =
    target instanceof Element
      ? target.closest('.wyrd-wishlist-drawer-close')
      : null;

  if (!closeButton) return;
  wyrdCloseWishlistDrawerV38(event);
});

function wyrdRepairLoyaltyVisitLabelV38() {
  const root = document.querySelector('#loyaltylion');
  if (!root) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];

  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((node) => {
    const value = node.nodeValue || '';
    if (value.replace(/\s+/g, ' ').trim().toLowerCase() !== 'visit') return;

    const leadingSpace = value.match(/^\s*/)?.[0] || '';
    const trailingSpace = value.match(/\s*$/)?.[0] || '';
    node.nodeValue = `${leadingSpace}Visit the Wyrd store${trailingSpace}`;
  });
}

function wyrdScheduleLoyaltyRepairV38() {
  if (wyrdLoyaltyRepairFrameV38) return;

  wyrdLoyaltyRepairFrameV38 = window.requestAnimationFrame(() => {
    wyrdLoyaltyRepairFrameV38 = 0;
    wyrdRepairLoyaltyVisitLabelV38();
  });
}

const wyrdWishlistObserverV38 = new MutationObserver((mutations) => {
  wyrdScheduleLoyaltyRepairV38();
  wyrdScheduleWishlistDrawerRepairV38();

  const hasNewWishlistButton = mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some(
      (node) =>
        node instanceof Element &&
        (node.matches(wyrdWishlistButtonSelectorV38) ||
          node.querySelector(wyrdWishlistButtonSelectorV38))
    )
  );

  if (!hasNewWishlistButton || wyrdWishlistMutationFrameV38) return;

  wyrdWishlistMutationFrameV38 = window.requestAnimationFrame(() => {
    wyrdWishlistMutationFrameV38 = 0;
    wyrdScheduleWishlistButtonInitializationV38();
    wyrdScheduleWishlistRefreshV38();
  });
});

wyrdWishlistObserverV38.observe(document.documentElement, {
  childList: true,
  subtree: true,
  characterData: true
});

wyrdScheduleLoyaltyRepairV38();
wyrdScheduleWishlistDrawerRepairV38();

window.addEventListener('hashchange', wyrdScheduleWishlistDrawerRepairV38);
window.addEventListener('popstate', wyrdScheduleWishlistDrawerRepairV38);
window.addEventListener('pageshow', () => {
  wyrdScheduleWishlistButtonInitializationV38();
  wyrdScheduleWishlistRefreshV38(60);
  wyrdScheduleLoyaltyRepairV38();
  wyrdScheduleWishlistDrawerRepairV38();
});
})();